import { trackConversion } from 'goaffpro-client';
import { Toast } from '../../../../components/Common/Toast';
import { PlanDetailsModel } from '../../../../interfaces/plan.interface';
import { callAPI, reloadToken } from '../../../../services/api.service';
import { cleanObject } from '../../../../helper/helper';
import { createUserWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { CardElement } from '@stripe/react-stripe-js';
import { ApplyPromoCodeStates, UserData } from './SignUpData.interface';
import { mapToUserData } from './SignUp.mapper';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import { auth } from '../../../../firebase';
import { AppDispatch } from '../../../../store/store';
import {
  setRedirectURL,
  setShowLogin,
  setShowRecreateUserSignup,
  setStopLoadUpdates,
  setUser,
} from '../../../../store/userSlice';
import { UserModel } from '../../../../interfaces/user.interface';
import { TokenCookieManager } from '../../../../utils/tokenCookies';
import { HTTPStatusEnum } from '../../../../interfaces/log.interface';
import { CouponTypesEnum } from '../../../../interfaces/coupon.interface';
import {
  autoFetchUserDetails,
  forceFetchUserDetails,
  unsubscribeUserSubscriptions,
} from '../../../../store/thunks/user.thunk';

export const applyPromoCode = async (
  couponCode: string,
  selectedPlanDescription: PlanDetailsModel,
  setStates: ApplyPromoCodeStates,
) => {
  const { setCouponCode, setPrice, setCouponLoader, setCouponClass, setCouponDescription, setCouponDiscount } =
    setStates;

  setCouponCode(null);
  setPrice(selectedPlanDescription?.cost);
  setCouponLoader(true);

  const response = await callAPI('user/coupon-validate', {
    couponCode,
    amount: selectedPlanDescription?.cost,
    requestType: CouponTypesEnum.FOR_PLAN_PURCHASE,
  });

  if (response.status === HTTPStatusEnum.OK) {
    const discountedPrice = parseFloat(response?.data?.discount).toFixed(2);
    setCouponClass('text-storygreen');
    setCouponDescription(response?.data?.couponDescription);
    setCouponDiscount(discountedPrice);
    setPrice(parseFloat(response?.data?.price).toFixed(2));
    Toast('Coupon Applied');
    setCouponCode(couponCode);
  } else {
    setCouponCode(null);
    setCouponClass('text-storyred');
    setCouponDiscount(null);
    Toast('Invalid Coupon', true);
  }

  setCouponLoader(false);
};

export const signUpNewUser = async (
  isRecreateUser: boolean,
  values: UserData,
  accessKey: string | null,
  subscribePlanId: string | null,
  subscribePlanCurrency: string | undefined,
  inviteAccessKey: string | null,
  signupViaGoogle: UserData | null,
  user: UserModel | any,
  setSignupLoader: (value: boolean) => void,
  dispatch: AppDispatch,
  navigateTo: (url: string) => void,
  resetForm?: () => void,
): Promise<void> => {
  setSignupLoader(true);
  dispatch(setStopLoadUpdates(true));
  try {
    const userData = mapToUserData({
      signupViaGoogle,
      values,
      user,
      currency: subscribePlanCurrency,
    });

    if (!userData?.email) {
      Toast('Email is required', true);
      setSignupLoader(false);
      return;
    }

    const apiURL = isRecreateUser ? 'api/auth/reset' : 'api/auth/signUp';
    const response = await callAPI(
      apiURL,
      userData,
      'POST',
      cleanObject({
        'Content-Type': 'application/json',
        access_key: accessKey,
        subscribe_plan_id: subscribePlanId,
        invite_access_token: inviteAccessKey,
      }),
    );

    if (!response.sessionURL) {
      dispatch(setStopLoadUpdates(false));
    }

    /// handle case when user is already registered and trying to signup again via google
    /// and not create new auth connection with firebase
    if (response.status !== HTTPStatusEnum.OK) {
      if (!(signupViaGoogle && response.status === HTTPStatusEnum.CONFLICT)) {
        dispatch(unsubscribeUserSubscriptions());
      }
    }
    if (response?.status === HTTPStatusEnum.UNPROCESSABLE_ENTITY) {
      Toast('A user with this email already exists in the system. Please go to the login page to sign in.', true);
      setSignupLoader(false);
    } else if (response?.status === HTTPStatusEnum.BAD_REQUEST_DATA) {
      Toast(response.message || 'Failed to register. Please try again', true);
      setSignupLoader(false);
    } else if (response?.error || response?.status === HTTPStatusEnum.CONFLICT) {
      dispatch(setShowRecreateUserSignup({ show: true }));
    } else if (response?.error || response?.status === HTTPStatusEnum.FORBIDDEN) {
      Toast('User Not Registered', true);
    } else if (response?.status === HTTPStatusEnum.BAD_REQUEST) {
      Toast('Please Provide a Valid Email ', true);
    } else if (response?.verified) {
      Toast(response?.message, true);
    } else if (!signupViaGoogle?.firebaseId && response?.status === HTTPStatusEnum.OK) {
      const firebaseResponse = await createUserWithEmailAndPassword(auth, values.email, values.password);
      dispatch(setUser(firebaseResponse?.user));

      await callAPI('api/update-firebase-id', {
        email: userData?.email,
        firebaseId: firebaseResponse?.user?.uid,
      });

      TokenCookieManager.removeToken();
      await reloadToken();

      // const idTokenResult = await firebaseResponse?.user?.getIdTokenResult();
      // const idToken = idTokenResult.token;
      //
      // const encryptedToken = encryptToken(idToken, process.env.REACT_APP_TOKEN_ENCRYPT_KEY!);
      //
      // localStorage.setItem(UserLocalStorageKeysEnum.TOKEN, encryptedToken);
    } else {
      Toast('User Registered Successfully');
    }

    if (response.status === HTTPStatusEnum.OK) {
      if (response.sessionURL) {
        resetForm && resetForm();

        dispatch(
          forceFetchUserDetails(() => {
            dispatch(setRedirectURL(response.sessionURL));
            setSignupLoader(false);
          }),
        );
        return;
      }
      dispatch(setStopLoadUpdates(false));
      dispatch(autoFetchUserDetails());
      navigateTo('/');
      dispatch(setShowLogin(false));
    }

    setSignupLoader(false);
  } catch (err) {
    Toast('Error Signing Up! Check details and try again!', true);
    setSignupLoader(false);
  }
};

export const submitUserForm = async (
  values: UserData,
  signupViaGoogle: UserData | null,
  selectedPlan: string,
  promoPlan: string | null,
  couponCode: string | null,
  selectedPlanDescription: PlanDetailsModel | null,
  user: UserModel | any,
  stripe: Stripe | null,
  elements: StripeElements | null,
  setSignupLoader: (value: boolean) => void,
  dispatch: AppDispatch,
) => {
  setSignupLoader(true);
  try {
    if (promoPlan) {
      const userData = mapToUserData({ signupViaGoogle, values, selectedPlan, couponCode, user });

      const response = await callAPI('api/createUser', userData);
      if (response?.error || response?.status === HTTPStatusEnum.FORBIDDEN) {
        Toast('User Not Registered', true);
        setSignupLoader(false);
      } else if (response?.status === HTTPStatusEnum.BAD_REQUEST) {
        Toast('Please Provide a Valid Email', true);
        setSignupLoader(false);
      } else if (response?.verified) {
        Toast(response?.message, true);
        setSignupLoader(false);
      } else if (!signupViaGoogle?.firebaseId) {
        const firebaseResponse = await createUserWithEmailAndPassword(auth, values.email, values.password);
        dispatch(setUser(firebaseResponse?.user));

        await callAPI('api/update-firebase-id', {
          email: userData?.email,
          firebaseId: firebaseResponse?.user?.uid,
        });

        TokenCookieManager.removeToken();
        await reloadToken();
        Toast('User Registered Successfully');
      }
    } else {
      if (!stripe || !elements) {
        Toast('Stripe not loaded', true);
        setSignupLoader(false);
        return;
      }
      const card = elements.getElement(CardElement);
      const resultStripe =
        card &&
        (await stripe.createPaymentMethod({
          type: 'card',
          card: card,
          billing_details: {
            email: values.email,
          },
        }));

      if (resultStripe?.error) {
        Toast('Error Processing Card details', true);
        setSignupLoader(false);
      } else {
        const userData = mapToUserData({
          signupViaGoogle,
          values,
          selectedPlan,
          couponCode,
          user,
          paymentMethod: resultStripe?.paymentMethod?.id,
        });

        const response = await callAPI('api/createUser', userData);
        if (response?.error || response?.status === HTTPStatusEnum.FORBIDDEN) {
          Toast('User Not Registered', true);
          setSignupLoader(false);
        } else if (response?.status === HTTPStatusEnum.BAD_REQUEST) {
          Toast('Please Provide a Valid Email ', true);
          setSignupLoader(false);
        } else if (response?.verified) {
          Toast(response?.message, true);
          setSignupLoader(false);
        } else if (!signupViaGoogle?.firebaseId) {
          const firebaseResponse = await createUserWithEmailAndPassword(auth, values.email, values.password);
          dispatch(setUser(firebaseResponse?.user));

          await callAPI('api/update-firebase-id', {
            email: userData?.email,
            firebaseId: firebaseResponse?.user?.uid,
          });

          TokenCookieManager.removeToken();
          await reloadToken();

          trackConversion({
            number: resultStripe?.paymentMethod?.id!, // human readable order number displayed to the admins
            total: Number(selectedPlanDescription?.cost), // the order total (the final amount that the customer paid)
            currency: 'USD', //ISO-4217 three letter currency code of the order
          });
          Toast('User Registered Successfully');
          dispatch(setShowLogin(false));
        } else {
          trackConversion({
            number: resultStripe?.paymentMethod?.id!, // human readable order number displayed to the admins
            total: Number(selectedPlanDescription?.cost), // the order total (the final amount that the customer paid)
            currency: 'USD', //ISO-4217 three letter currency code of the order
          });
        }
      }
    }
    setSignupLoader(false);
  } catch (err) {
    Toast('Error Signing Up! Check details and try again!', true);
    setSignupLoader(false);
  }
};

export const getUserAuthSession = async (): Promise<UserModel | null> => {
  return new Promise((resolve) => {
    const unsubscribeAuthSession = onAuthStateChanged(auth, async (currentUser: any) => {
      unsubscribeAuthSession();
      resolve(currentUser);
    });
  });
};
